import { useEffect, useRef, useState } from "react";
import { getAdminHeaders, uploadAdminImage, toAssetUrl } from "./adminApi";
import { adminDutySyncStatus, adminSyncDutyWeek } from "../../api/coReadApi";

const emptyReading = {
  period_label: "",
  book_title: "",
  book_author: "",
  summary: "",
  schedule_note: "每日 07:00 - 08:00",
  start_date: "",
  end_date: "",
  cover_url: "",
  is_current: 0,
  is_enabled: 1,
  sort_order: 0,
};

export default function AdminBookClub() {
  const [settings, setSettings] = useState(null);
  const [settingsForm, setSettingsForm] = useState({});
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(emptyReading);
  const [message, setMessage] = useState("");
  const [coverUploading, setCoverUploading] = useState(false);
  const coverInputRef = useRef(null);

  const pageSize = 20;

  const loadSettings = async () => {
    const res = await fetch("/api/admin/book-club/settings", { headers: getAdminHeaders() });
    const data = await res.json();
    if (res.ok) {
      setSettings(data);
      setSettingsForm(data);
    }
  };

  const loadItems = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
      const res = await fetch(`/api/admin/book-club/readings?${params}`, {
        headers: getAdminHeaders(),
      });
      const data = await res.json();
      if (res.ok) {
        setItems(data.items || []);
        setTotal(data.total || 0);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  useEffect(() => {
    loadItems();
  }, [page]);

  const saveSettings = async (e) => {
    e.preventDefault();
    const res = await fetch("/api/admin/book-club/settings", {
      method: "PATCH",
      headers: getAdminHeaders(true),
      body: JSON.stringify(settingsForm),
    });
    const data = await res.json();
    if (!res.ok) {
      alert(data.detail || "保存失败");
      return;
    }
    setSettings(data.settings);
    setMessage("页面设置已保存");
  };

  const openCreate = () => {
    setCreating(true);
    setEditing(null);
    setForm({ ...emptyReading, sort_order: total + 1 });
  };

  const openEdit = (item) => {
    setEditing(item);
    setCreating(false);
    setForm({
      period_label: item.period_label,
      book_title: item.book_title,
      book_author: item.book_author || "",
      summary: item.summary || "",
      schedule_note: item.schedule_note || "",
      start_date: item.start_date || "",
      end_date: item.end_date || "",
      cover_url: item.cover_url || "",
      is_current: item.is_current,
      is_enabled: item.is_enabled,
      sort_order: item.sort_order,
    });
  };

  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCoverUploading(true);
    try {
      const url = await uploadAdminImage(file);
      setForm((prev) => ({ ...prev, cover_url: url }));
    } catch (err) {
      alert(err.message || "上传失败");
    } finally {
      setCoverUploading(false);
      if (coverInputRef.current) coverInputRef.current.value = "";
    }
  };

  const saveReading = async () => {
    if (!form.period_label.trim() || !form.book_title.trim()) {
      alert("请填写期次名称和书名");
      return;
    }
    const payload = {
      ...form,
      is_current: Number(form.is_current),
      is_enabled: Number(form.is_enabled),
      sort_order: Number(form.sort_order),
    };
    const url = editing
      ? `/api/admin/book-club/readings/${editing.id}`
      : "/api/admin/book-club/readings";
    const res = await fetch(url, {
      method: editing ? "PATCH" : "POST",
      headers: getAdminHeaders(true),
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    if (!res.ok) {
      alert(data.detail || "保存失败");
      return;
    }
    setEditing(null);
    setCreating(false);
    setMessage(editing ? "期次已更新" : "期次已添加");
    loadItems();
  };

  const removeReading = async (item) => {
    if (!window.confirm(`删除「${item.period_label} · ${item.book_title}」？`)) return;
    const res = await fetch(`/api/admin/book-club/readings/${item.id}`, {
      method: "DELETE",
      headers: getAdminHeaders(),
    });
    if (!res.ok) {
      const data = await res.json();
      alert(data.detail || "删除失败");
      return;
    }
    setMessage("已删除");
    loadItems();
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const showModal = creating || editing;

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-gray-800">读书会管理</h1>
        <p className="text-sm text-gray-500 mt-1">
          配置晨读页面文案与每期书目。前台以「好书共读」中性展示，书名与简介由您维护。
        </p>
      </div>

      {message && (
        <div className="bg-green-50 text-green-700 text-sm px-4 py-2 rounded-lg">{message}</div>
      )}

      <form onSubmit={saveSettings} className="bg-white rounded-xl shadow p-6 space-y-4">
        <h2 className="font-bold text-gray-800">页面设置</h2>
        <div className="grid md:grid-cols-2 gap-4">
          {[
            ["page_title", "页面标题", "晨读读书会"],
            ["page_subtitle", "副标题", "每天清晨，与一本好书相遇"],
            ["schedule_label", "固定时段", "每日 07:00 - 08:00"],
            ["meta_title", "SEO 标题", ""],
          ].map(([key, label, placeholder]) => (
            <label key={key} className="block text-sm">
              <span className="text-gray-600">{label}</span>
              <input
                value={settingsForm[key] || ""}
                onChange={(e) => setSettingsForm({ ...settingsForm, [key]: e.target.value })}
                placeholder={placeholder}
                className="mt-1 w-full border rounded-lg px-3 py-2"
              />
            </label>
          ))}
        </div>
        {[
          ["intro_text", "页面简介"],
          ["join_note", "参与说明"],
          ["meta_description", "SEO 描述"],
          ["meta_keywords", "SEO 关键词"],
        ].map(([key, label]) => (
          <label key={key} className="block text-sm">
            <span className="text-gray-600">{label}</span>
            <textarea
              value={settingsForm[key] || ""}
              onChange={(e) => setSettingsForm({ ...settingsForm, [key]: e.target.value })}
              rows={key.startsWith("meta_") ? 2 : 3}
              className="mt-1 w-full border rounded-lg px-3 py-2"
            />
          </label>
        ))}
        <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm">
          保存页面设置
        </button>
      </form>

      <div className="flex items-center justify-between">
        <h2 className="font-bold text-gray-800">期次书目</h2>
        <button
          onClick={openCreate}
          className="px-4 py-2 bg-pink-500 text-white rounded-lg text-sm"
        >
          添加新一期
        </button>
      </div>

      <div className="bg-white rounded-xl shadow overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500">
            <tr>
              <th className="text-left p-3">期次</th>
              <th className="text-left p-3">书名</th>
              <th className="text-left p-3">作者</th>
              <th className="text-left p-3">状态</th>
              <th className="text-left p-3">操作</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} className="p-8 text-center text-gray-400">
                  加载中...
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={5} className="p-8 text-center text-gray-400">
                  暂无期次，请添加
                </td>
              </tr>
            ) : (
              items.map((item) => (
                <tr key={item.id} className="border-t">
                  <td className="p-3">{item.period_label}</td>
                  <td className="p-3 font-medium text-gray-800">{item.book_title}</td>
                  <td className="p-3 text-gray-500">{item.book_author || "—"}</td>
                  <td className="p-3">
                    {item.is_current ? (
                      <span className="text-pink-600 font-medium">当期</span>
                    ) : item.is_enabled ? (
                      <span className="text-gray-500">展示</span>
                    ) : (
                      <span className="text-gray-400">隐藏</span>
                    )}
                  </td>
                  <td className="p-3 space-x-2">
                    <button
                      type="button"
                      onClick={() => openEdit(item)}
                      className="text-blue-600 hover:underline"
                    >
                      编辑
                    </button>
                    <button
                      type="button"
                      onClick={() => removeReading(item)}
                      className="text-red-500 hover:underline"
                    >
                      删除
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex justify-center gap-3">
          <button
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="px-4 py-2 border rounded-lg text-sm disabled:opacity-40"
          >
            上一页
          </button>
          <span className="text-sm text-gray-500 self-center">
            {page} / {totalPages}
          </span>
          <button
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            className="px-4 py-2 border rounded-lg text-sm disabled:opacity-40"
          >
            下一页
          </button>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6 space-y-4">
            <h3 className="font-bold text-lg">{editing ? "编辑期次" : "添加新一期"}</h3>
            {[
              ["period_label", "期次名称", "如：第 12 期"],
              ["book_title", "书名", ""],
              ["book_author", "作者", "可选"],
              ["schedule_note", "时段说明", "每日 07:00 - 08:00"],
              ["start_date", "开始日期", "YYYY-MM-DD，可选"],
              ["end_date", "结束日期", "YYYY-MM-DD，可选"],
              ["sort_order", "排序（越小越靠前）", "0"],
            ].map(([key, label, placeholder]) => (
              <label key={key} className="block text-sm">
                <span className="text-gray-600">{label}</span>
                <input
                  value={form[key] ?? ""}
                  onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                  placeholder={placeholder}
                  className="mt-1 w-full border rounded-lg px-3 py-2"
                />
              </label>
            ))}
            <div className="block text-sm space-y-2">
              <span className="text-gray-600">书本封面</span>
              {form.cover_url ? (
                <img
                  src={toAssetUrl(form.cover_url)}
                  alt="封面预览"
                  className="w-24 h-32 object-cover rounded-lg border"
                />
              ) : (
                <p className="text-xs text-gray-400">未上传封面</p>
              )}
              <input
                ref={coverInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleCoverUpload}
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => coverInputRef.current?.click()}
                  disabled={coverUploading}
                  className="px-3 py-1.5 border rounded-lg text-sm disabled:opacity-50"
                >
                  {coverUploading ? "上传中..." : "上传封面图片"}
                </button>
                {form.cover_url && (
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, cover_url: "" })}
                    className="px-3 py-1.5 text-red-500 text-sm"
                  >
                    移除
                  </button>
                )}
              </div>
            </div>
            <label className="block text-sm">
              <span className="text-gray-600">内容简介</span>
              <textarea
                value={form.summary}
                onChange={(e) => setForm({ ...form, summary: e.target.value })}
                rows={5}
                className="mt-1 w-full border rounded-lg px-3 py-2"
              />
            </label>
            <div className="flex flex-wrap gap-4 text-sm">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={Number(form.is_current) === 1}
                  onChange={(e) =>
                    setForm({ ...form, is_current: e.target.checked ? 1 : 0 })
                  }
                />
                设为当期共读
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={Number(form.is_enabled) === 1}
                  onChange={(e) =>
                    setForm({ ...form, is_enabled: e.target.checked ? 1 : 0 })
                  }
                />
                在前台展示
              </label>
            </div>
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={saveReading}
                className="px-4 py-2 bg-pink-500 text-white rounded-lg text-sm"
              >
                保存
              </button>
              <button
                type="button"
                onClick={() => {
                  setEditing(null);
                  setCreating(false);
                }}
                className="px-4 py-2 border rounded-lg text-sm"
              >
                取消
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="border-t pt-8">
        <DutyWeekAdmin readings={items} />
      </div>

      <div className="border-t pt-8">
        <ShareAdminList />
      </div>
    </div>
  );
}

function DutyWeekAdmin({ readings }) {
  const [readingId, setReadingId] = useState("");
  const [weeks, setWeeks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncMap, setSyncMap] = useState({});
  const [syncingId, setSyncingId] = useState(null);
  const [form, setForm] = useState({
    week_label: "",
    week_number: 1,
    start_date: "",
  });
  const [editingDays, setEditingDays] = useState(null);

  const loadWeeks = async (rid) => {
    if (!rid) {
      setWeeks([]);
      setSyncMap({});
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`/api/admin/book-club/duty-weeks?reading_id=${rid}`, {
        headers: getAdminHeaders(),
      });
      const data = await res.json();
      if (res.ok) {
        const list = data.items || [];
        setWeeks(list);
        try {
          const st = await adminDutySyncStatus(list.map((w) => w.id));
          setSyncMap(st.items || {});
        } catch {
          setSyncMap({});
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const syncWeek = async (week) => {
    if (!week?.id) return;
    setSyncingId(week.id);
    try {
      const data = await adminSyncDutyWeek(week.id);
      alert(
        data.sync_message ||
          `已同步到共读中心：${data.week_label || week.week_label}（v${data.version || 1}）`
      );
      await loadWeeks(readingId);
    } catch (e) {
      alert(e.message || "同步失败");
    } finally {
      setSyncingId(null);
    }
  };

  useEffect(() => {
    if (!readingId && readings?.length) {
      const current = readings.find((r) => r.is_current === 1) || readings[0];
      setReadingId(String(current.id));
    }
  }, [readings]);

  useEffect(() => {
    if (readingId) loadWeeks(readingId);
  }, [readingId]);

  const createWeek = async (e) => {
    e.preventDefault();
    if (!readingId || !form.week_label.trim() || !form.start_date) {
      alert("请选择期次，并填写周次名称与开始日期（周一）");
      return;
    }
    const res = await fetch("/api/admin/book-club/duty-weeks", {
      method: "POST",
      headers: getAdminHeaders(true),
      body: JSON.stringify({
        reading_id: Number(readingId),
        week_label: form.week_label.trim(),
        week_number: Number(form.week_number) || 1,
        start_date: form.start_date,
      }),
    });
    const data = await res.json();
    if (!res.ok) {
      alert(data.detail || "创建失败");
      return;
    }
    setForm({ week_label: "", week_number: (Number(form.week_number) || 1) + 1, start_date: "" });
    loadWeeks(readingId);
  };

  const removeWeek = async (week) => {
    if (!window.confirm(`确定删除「${week.week_label}」？`)) return;
    const res = await fetch(`/api/admin/book-club/duty-weeks/${week.id}`, {
      method: "DELETE",
      headers: getAdminHeaders(),
    });
    if (!res.ok) {
      const data = await res.json();
      alert(data.detail || "删除失败");
      return;
    }
    if (editingDays?.id === week.id) setEditingDays(null);
    loadWeeks(readingId);
  };

  const saveDay = async (day) => {
    const res = await fetch(`/api/admin/book-club/duty-days/${day.id}`, {
      method: "PATCH",
      headers: getAdminHeaders(true),
      body: JSON.stringify({
        host: day.host,
        singer: day.singer,
        projector: day.projector,
        reading_content: day.reading_content,
      }),
    });
    const data = await res.json();
    if (!res.ok) {
      alert(data.detail || "保存失败");
      return;
    }
    setEditingDays((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        days: prev.days.map((d) => (d.id === day.id ? data.item : d)),
      };
    });
    // 该周已同步过：保存日行后幂等再同步，保持共读中心计划最新
    const weekId = editingDays?.id;
    if (weekId && syncMap[weekId]?.week_plan_id) {
      try {
        await adminSyncDutyWeek(weekId);
        const st = await adminDutySyncStatus([weekId]);
        setSyncMap((prev) => ({ ...prev, ...(st.items || {}) }));
      } catch {
        /* 保存已成功；同步失败不阻断 */
      }
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="font-bold text-gray-800">值日表（周次）</h2>
        <p className="text-sm text-gray-500 mt-1">
          按周一创建一周七天：工作日可填主持/唱歌/放映/读书计划，周末默认休息。会员也可在前台自行填写。
          填完整后点「同步到共读中心」，成员即可在 AI 共读中心看到本周计划并打卡。
        </p>
      </div>

      <div className="bg-white rounded-xl shadow p-6 space-y-4">
        <label className="block text-sm max-w-md">
          <span className="text-gray-600">所属期次</span>
          <select
            value={readingId}
            onChange={(e) => setReadingId(e.target.value)}
            className="mt-1 w-full border rounded-lg px-3 py-2"
          >
            <option value="">请选择</option>
            {(readings || []).map((r) => (
              <option key={r.id} value={r.id}>
                {r.period_label} · {r.book_title}
                {r.is_current ? "（当期）" : ""}
              </option>
            ))}
          </select>
        </label>

        <form onSubmit={createWeek} className="grid md:grid-cols-4 gap-3 items-end">
          <label className="block text-sm">
            <span className="text-gray-600">周次名称</span>
            <input
              value={form.week_label}
              onChange={(e) => setForm({ ...form, week_label: e.target.value })}
              placeholder="如：《论自由意志》第五周"
              className="mt-1 w-full border rounded-lg px-3 py-2"
            />
          </label>
          <label className="block text-sm">
            <span className="text-gray-600">周序号</span>
            <input
              type="number"
              min={1}
              value={form.week_number}
              onChange={(e) => setForm({ ...form, week_number: e.target.value })}
              className="mt-1 w-full border rounded-lg px-3 py-2"
            />
          </label>
          <label className="block text-sm">
            <span className="text-gray-600">开始日期（周一）</span>
            <input
              type="date"
              value={form.start_date}
              onChange={(e) => setForm({ ...form, start_date: e.target.value })}
              className="mt-1 w-full border rounded-lg px-3 py-2"
            />
          </label>
          <button type="submit" className="px-4 py-2 bg-amber-500 text-white rounded-lg text-sm">
            创建本周值日表
          </button>
        </form>

        {loading ? (
          <p className="text-gray-400 text-sm">加载中...</p>
        ) : weeks.length === 0 ? (
          <p className="text-gray-400 text-sm">该期次暂无值日周次</p>
        ) : (
          <div className="space-y-2">
            {weeks.map((w) => (
              <div
                key={w.id}
                className="flex flex-wrap items-center justify-between gap-2 border rounded-lg px-4 py-3"
              >
                <div>
                  <p className="font-medium text-gray-800">{w.week_label}</p>
                  <p className="text-xs text-gray-400">
                    第 {w.week_number} 周 · 起 {w.start_date}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-3 text-sm">
                  {syncMap[w.id]?.sync_status ? (
                    <span className="text-xs text-teal-700 bg-teal-50 px-2 py-0.5 rounded">
                      已同步
                      {syncMap[w.id].version ? ` v${syncMap[w.id].version}` : ""}
                    </span>
                  ) : (
                    <span className="text-xs text-gray-400">未同步</span>
                  )}
                  <button
                    type="button"
                    disabled={syncingId === w.id}
                    onClick={() => syncWeek(w)}
                    className="text-amber-700 hover:underline disabled:opacity-50"
                  >
                    {syncingId === w.id ? "同步中…" : "同步到共读中心"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditingDays(w)}
                    className="text-blue-600 hover:underline"
                  >
                    编辑每日
                  </button>
                  <button
                    type="button"
                    onClick={() => removeWeek(w)}
                    className="text-red-500 hover:underline"
                  >
                    删除
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {editingDays && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-lg">{editingDays.week_label}</h3>
              <button
                type="button"
                onClick={() => setEditingDays(null)}
                className="text-gray-500 text-sm"
              >
                关闭
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-gray-50 text-gray-500">
                    <th className="border p-2">日期</th>
                    <th className="border p-2">礼拜</th>
                    <th className="border p-2">主持</th>
                    <th className="border p-2">唱歌</th>
                    <th className="border p-2">放映</th>
                    <th className="border p-2">读书计划</th>
                    <th className="border p-2">操作</th>
                  </tr>
                </thead>
                <tbody>
                  {(editingDays.days || []).map((day) => (
                    <tr key={day.id}>
                      <td className="border p-2 text-center">{day.date_label}</td>
                      <td className="border p-2 text-center">{day.weekday_label}</td>
                      {["host", "singer", "projector", "reading_content"].map((key) => (
                        <td key={key} className="border p-1">
                          <input
                            value={day[key] || ""}
                            onChange={(e) =>
                              setEditingDays((prev) => ({
                                ...prev,
                                days: prev.days.map((d) =>
                                  d.id === day.id ? { ...d, [key]: e.target.value } : d
                                ),
                              }))
                            }
                            className="w-full border rounded px-2 py-1 text-center"
                          />
                        </td>
                      ))}
                      <td className="border p-2 text-center">
                        <button
                          type="button"
                          onClick={() => saveDay(day)}
                          className="text-blue-600 hover:underline"
                        >
                          保存
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ShareAdminList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadShares = async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/admin/book-club/shares?page=1&page_size=50", {
        headers: getAdminHeaders(),
      });
      const data = await res.json();
      if (res.ok) setItems(data.items || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadShares();
  }, []);

  const patchShare = async (id, body) => {
    await fetch(`/api/admin/book-club/shares/${id}`, {
      method: "PATCH",
      headers: getAdminHeaders(true),
      body: JSON.stringify(body),
    });
    loadShares();
  };

  const deleteShare = async (item) => {
    if (!window.confirm("删除这条分享？")) return;
    await fetch(`/api/admin/book-club/shares/${item.id}`, {
      method: "DELETE",
      headers: getAdminHeaders(),
    });
    loadShares();
  };

  if (loading) return <p className="text-gray-400 text-sm">加载中...</p>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-bold text-gray-800">会员阅读分享</h2>
          <p className="text-sm text-gray-500">审核留言，可将优质分享设为「精选」</p>
        </div>
        <button type="button" onClick={loadShares} className="px-3 py-1.5 border rounded-lg text-sm">
          刷新
        </button>
      </div>
      {items.length === 0 ? (
        <p className="text-gray-400 text-sm">暂无会员分享</p>
      ) : (
    <div className="space-y-3">
      {items.map((item) => (
        <div key={item.id} className="bg-white rounded-xl shadow p-4 text-sm space-y-2">
          <div className="flex flex-wrap gap-2 text-xs text-gray-500">
            <span className="font-medium text-gray-800">{item.author_name}</span>
            <span>· {item.period_label}《{item.book_title}》</span>
            {item.share_date && <span>· 阅读日 {item.share_date}</span>}
          </div>
          <p className="text-gray-700 whitespace-pre-line">{item.content}</p>
          <div className="flex flex-wrap gap-2 text-xs">
            <button
              type="button"
              onClick={() => patchShare(item.id, { is_featured: item.is_featured ? 0 : 1 })}
              className={`px-3 py-1 border rounded-lg ${item.is_featured ? "bg-amber-100 border-amber-300" : ""}`}
            >
              {item.is_featured ? "取消精选" : "设为精选"}
            </button>
            <button
              type="button"
              onClick={() => patchShare(item.id, { is_approved: item.is_approved ? 0 : 1 })}
              className="px-3 py-1 border rounded-lg"
            >
              {item.is_approved ? "隐藏" : "显示"}
            </button>
            <button
              type="button"
              onClick={() => deleteShare(item)}
              className="px-3 py-1 text-red-500 border rounded-lg"
            >
              删除
            </button>
          </div>
        </div>
      ))}
    </div>
      )}
    </div>
  );
}
